import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { fromPromise } from 'xstate5';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';

import {
  type ConvertToGroupOrderParams,
  type ConvertToGroupOrderResponse,
  type OrderingMachineAction,
} from '@order/features-ordering';

import { useBagConvertToGroupOrderMutation } from '../../GraphQL/BagGroupOrder.generated';
import { prepareCart } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useConvertToGroupOrder = () => {
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const [, executeConvertToGroupOrder] = useBagConvertToGroupOrderMutation();

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const convertToGroupOrder = useMemo(
    () =>
      fromPromise<ConvertToGroupOrderResponse, ConvertToGroupOrderParams>(async ({ input }) => {
        const { hostName } = input;

        const response = await executeConvertToGroupOrder({
          input: { multipartyOrderDetails: { hostName } },
        });

        const result = response.data?.convertToMultipartyOrder;
        const order =
          result?.__typename === 'ConvertToMultipartyOrderSuccess' ? result.order : undefined;

        if (response.error || !order) throw new Error('Unable to create group order');

        const cart = prepareCart(order as Partial<Order>);

        if (!cart.groupOrderJoinCode) throw new Error('Unable to create group order');

        return { cart };
      }),
    [executeConvertToGroupOrder],
  );

  const handleConvertToGroupOrderError = useCallback<OrderingMachineAction>(() => {
    addNoticeBanner(
      {
        id: GROUP_ORDER_CONVERT_ERROR_BANNER_ID,
        text: formatMessage(messages.convertGroupOrderError),
        palette: 'caution',
        autoHideTimeout: 1800,
      },
      true,
    );
  }, [addNoticeBanner, formatMessage]);

  return {
    convertToGroupOrder,
    handleConvertToGroupOrderError,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  convertGroupOrderError: {
    defaultMessage: 'Unable to create group order',
    description: 'Bag | Convert to group order | Error message',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const GROUP_ORDER_CONVERT_ERROR_BANNER_ID = 'group-order-convert-error-banner';
